// src/data/amenitiesClient.js
//
// EXPERIENCE-TAB: Client-side data access for amenity data.
// Mirrors the pattern used in peopleClient.js and locationScoreClient.js.

import { NetworkError, errorFromResponse } from "./errors.js";

const BASE      = "/api/amenities";
const ADMIN_BASE = "/api/admin/amenities";

/**
 * getAmenities(locationId)
 *
 * GET /api/amenities/:locationId
 * Returns the amenity row (with experienceScore computed inline by the
 * server if the row exists) or null when no data has been recorded.
 *
 * @param {string} locationId
 * @returns {Promise<object|null>}
 */
export async function getAmenities(locationId) {
  let res;
  try {
    res = await fetch(`${BASE}/${encodeURIComponent(locationId)}`);
  } catch {
    throw new NetworkError("Network error fetching amenities.");
  }
  if (res.status === 404) return null;
  if (!res.ok) throw await errorFromResponse(res);
  return res.json();
}

/**
 * putAmenities(locationId, amenities)
 *
 * PUT /api/amenities/:locationId
 * Upserts the full amenity set. `amenities` is a plain object keyed by
 * catalog key with `{ present, detail? }` values. Returns the updated
 * row including the recomputed experience score.
 *
 * Editor+ only. Throws on auth / validation errors (same error types as
 * leasesClient).
 *
 * @param {string} locationId
 * @param {object} amenities
 * @returns {Promise<object>}
 */
export async function putAmenities(locationId, amenities) {
  let res;
  try {
    res = await fetch(`${BASE}/${encodeURIComponent(locationId)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(amenities),
    });
  } catch {
    throw new NetworkError("Network error saving amenities.");
  }
  if (!res.ok) throw await errorFromResponse(res);
  return res.json();
}

/**
 * postBulkSetAmenity({ amenityKey, present, locationIds })
 *
 * POST /api/admin/amenities/bulk-set
 * Admin-only. Merges a single amenity key into the amenities JSONB for
 * every location in the supplied list. Existing amenity keys are preserved.
 * Unknown or deleted locationIds are skipped (reported in the response).
 *
 * @param {{ amenityKey: string, present: boolean, locationIds: string[] }} payload
 * @returns {Promise<{ updated: number, skipped: number, updatedLocationIds: string[] }>}
 */
export async function postBulkSetAmenity(payload) {
  let res;
  try {
    res = await fetch(`${ADMIN_BASE}/bulk-set`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new NetworkError("Network error during amenity bulk-set.");
  }
  if (!res.ok) throw await errorFromResponse(res);
  return res.json();
}
